import React from 'react';
import { UFO_SIZES, UFO_COLORS, TRACTOR_BEAM_COLOR, TRACTOR_BEAM_OPACITY, UFO_HOVER_AMPLITUDE, UFO_HOVER_FREQUENCY, EMOJI_FLOAT_DURATION } from '../../utils/constants';

interface UfoIconProps {
  rotation?: number;
  emoji: string;
  ufoType: 'basic' | 'armored' | 'stealth';
  size?: number;
  isRescueAnimation?: boolean;
  animationOffset?: number; // For individual animation timing
}

const UfoIcon: React.FC<UfoIconProps> = ({ 
  rotation = 0, 
  emoji, 
  ufoType, 
  size, 
  isRescueAnimation = false,
  animationOffset = 0
}) => {
  const actualSize = (size || UFO_SIZES[ufoType]) * 1.5;
  const colors = UFO_COLORS[ufoType];
  
  // Calculate hover animation offset for subtle floating effect
  const hoverOffset = Math.sin(Date.now() * UFO_HOVER_FREQUENCY + animationOffset) * UFO_HOVER_AMPLITUDE;
  
  const baseTransform = `rotate(${rotation}deg) translateY(${hoverOffset}px)`;
  const rescueTransform = isRescueAnimation ? 'scale(1.2) rotate(10deg)' : '';
  const finalTransform = `${baseTransform} ${rescueTransform}`;

  return (
    <div style={{ 
      transform: finalTransform, 
      transition: isRescueAnimation ? 'transform 0.3s ease-out' : 'transform 0.1s linear',
      position: 'relative',
      width: actualSize,
      height: actualSize
    }}>
      {/* Main UFO SVG */}
      <svg width={actualSize} height={actualSize} viewBox="0 0 48 48">
        {/* Tractor Beam - rendered first so it appears behind */}
        <defs>
          <linearGradient id={`tractor-beam-${ufoType}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={TRACTOR_BEAM_COLOR} stopOpacity="0" />
            <stop offset="30%" stopColor={TRACTOR_BEAM_COLOR} stopOpacity={TRACTOR_BEAM_OPACITY} />
            <stop offset="100%" stopColor={TRACTOR_BEAM_COLOR} stopOpacity={TRACTOR_BEAM_OPACITY * 0.7} />
          </linearGradient>
        </defs>
        
        {/* Tractor Beam Cone */}
        <path 
          d="M18 32 L30 32 L35 48 L13 48 Z" 
          fill={`url(#tractor-beam-${ufoType})`}
          opacity={isRescueAnimation ? 0.2 : 0.8}
        />
        
        {/* UFO Body based on type */}
        {ufoType === 'basic' && (
          <g>
            {/* Basic UFO Dome */}
            <ellipse cx="24" cy="20" rx="16" ry="8" fill={colors.dome} opacity="0.85" />
            {/* Basic UFO Base */}
            <ellipse cx="24" cy="26" rx="20" ry="6" fill={colors.base} />
            {/* Basic UFO Lights */}
            <circle cx="12" cy="26" r="2" fill={colors.lights} opacity="0.9">
              <animate attributeName="opacity" values="0.9;0.5;0.9" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx="24" cy="28" r="2" fill={colors.lights} opacity="0.9">
              <animate attributeName="opacity" values="0.5;0.9;0.5" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx="36" cy="26" r="2" fill={colors.lights} opacity="0.9">
              <animate attributeName="opacity" values="0.9;0.5;0.9" dur="2s" repeatCount="indefinite" />
            </circle>
          </g>
        )}
        
        {ufoType === 'armored' && (
          <g>
            {/* Armored UFO Dome */}
            <ellipse cx="24" cy="20" rx="16" ry="8" fill={colors.dome} opacity="0.9" />
            {/* Armor plating */}
            <rect x="16" y="18" width="16" height="2" fill="#37474F" opacity="0.8" />
            <rect x="18" y="20" width="12" height="2" fill="#37474F" opacity="0.8" />
            {/* Armored UFO Base */}
            <ellipse cx="24" cy="26" rx="20" ry="6" fill={colors.base} />
            {/* Armored lights (fewer, more intense) */}
            <circle cx="15" cy="26" r="2.5" fill={colors.lights} opacity="1">
              <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite" />
            </circle>
            <circle cx="33" cy="26" r="2.5" fill={colors.lights} opacity="1">
              <animate attributeName="opacity" values="0.3;1;0.3" dur="1.5s" repeatCount="indefinite" />
            </circle>
          </g>
        )}
        
        {ufoType === 'stealth' && (
          <g>
            {/* Stealth UFO Dome (angular design) */}
            <polygon points="8,20 24,12 40,20 24,24" fill={colors.dome} opacity="0.7" />
            {/* Stealth UFO Base (more angular) */}
            <polygon points="4,26 24,24 44,26 44,28 24,30 4,28" fill={colors.base} />
            {/* Stealth lights (minimal, fast blinking) */}
            <circle cx="18" cy="26" r="1.5" fill={colors.lights} opacity="0.8">
              <animate attributeName="opacity" values="0;1;0" dur="0.8s" repeatCount="indefinite" />
            </circle>
            <circle cx="30" cy="26" r="1.5" fill={colors.lights} opacity="0.8">
              <animate attributeName="opacity" values="1;0;1" dur="0.8s" repeatCount="indefinite" />
            </circle>
          </g>
        )}
        
        {/* UFO Details - Antenna */}
        <line x1="24" y1="12" x2="24" y2="8" stroke="#C0C0C0" strokeWidth="1" />
        <circle cx="24" cy="8" r="1.5" fill="#FFD700" />
      </svg>
      
      {/* Captured Emoji floating in tractor beam */}
      <div style={{
        position: 'absolute',
        top: '40%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: actualSize * 0.4,
        filter: isRescueAnimation ? 'brightness(1.5) drop-shadow(0 0 8px #FFD700)' : 'sepia(0.2) brightness(0.9)',
        animation: `emojiFloat ${EMOJI_FLOAT_DURATION}ms ease-in-out infinite`,
        zIndex: 1,
        opacity: isRescueAnimation ? 0.3 : 0.9
      }}>
        {emoji}
      </div>
      
      {/* CSS Animations */}
      <style>
        {`
          @keyframes emojiFloat {
            0%, 100% { transform: translate(-50%, -50%) translateY(0px) rotate(0deg); }
            25% { transform: translate(-50%, -50%) translateY(-2px) rotate(2deg); }
            50% { transform: translate(-50%, -50%) translateY(0px) rotate(0deg); }
            75% { transform: translate(-50%, -50%) translateY(2px) rotate(-2deg); }
          }
        `}
      </style>
    </div>
  );
};

export default UfoIcon; 